import { Button, FormGroup, TextField } from "@mui/material";
import { KeyValuePairInput } from "../../__generated__/globalTypes";
import { Add, DeleteForever } from "@mui/icons-material";
import HESPFormLabel from "../HESPFormLabel/HESPFormLabel";
import KeyValueConstructor from "../../KeyValueConstructor/KeyValueConstructor";
import { v4 } from "uuid";
import _ from "lodash";

export interface THEOadsAssetKey {
    tempId: string;
    assetKey: string;
    adBreakSelectionParameters: KeyValuePairInput[];
    eabnAdTagParameters: KeyValuePairInput[];
}

interface Props {
    assetKeys: THEOadsAssetKey[];
    onChangeKeys: (keys: THEOadsAssetKey[]) => void;
}

export default function THEOadsAssetKeys({ assetKeys, onChangeKeys }: Props) {
    function onAddAssetKey(): void {
        onChangeKeys(
            assetKeys.concat([
                {
                    tempId: v4(),
                    adBreakSelectionParameters: [],
                    assetKey: "",
                    eabnAdTagParameters: [],
                } as THEOadsAssetKey,
            ]),
        );
    }

    function changeAssetKey(id: string, key: string) {
        onChangeKeys(
            assetKeys.map((ak) => {
                if (ak.tempId === id) {
                    return {
                        ...ak,
                        assetKey: key,
                    };
                }
                return ak;
            }),
        );
    }

    function onRemoveAssetKey(id: string) {
        if (assetKeys.length > 1) {
            onChangeKeys(assetKeys.filter((ak) => ak.tempId !== id));
        }
    }

    function addAdBreakSelectionParam(id: string): void {
        onChangeKeys(
            assetKeys.map((ak) => {
                if (ak.tempId === id) {
                    return {
                        ...ak,
                        adBreakSelectionParameters: ak.adBreakSelectionParameters.concat([{ key: "", value: "" }]),
                    };
                }
                return ak;
            }),
        );
    }

    function addEabnAdTagParameters(id: string): void {
        onChangeKeys(
            assetKeys.map((ak) => {
                if (ak.tempId === id) {
                    return {
                        ...ak,
                        eabnAdTagParameters: ak.eabnAdTagParameters.concat([{ key: "", value: "" }]),
                    };
                }
                return ak;
            }),
        );
    }

    function updateAdBreakSelectionParam(id: string, type: "key" | "value", value: string, index: number): void {
        onChangeKeys(
            assetKeys.map((ak) => {
                if (ak.tempId === id) {
                    const currentItems = ak.adBreakSelectionParameters;
                    currentItems[index] = {
                        key: type === "key" ? value : currentItems[index].key,
                        value: type === "value" ? value : currentItems[index].value,
                    };
                    return {
                        ...ak,
                        adBreakSelectionParameters: currentItems,
                    };
                }
                return ak;
            }),
        );
    }

    function updateEabnAdTagParameters(id: string, type: "key" | "value", value: string, index: number): void {
        onChangeKeys(
            assetKeys.map((ak) => {
                if (ak.tempId === id) {
                    const currentItems = ak.eabnAdTagParameters;
                    currentItems[index] = {
                        key: type === "key" ? value : currentItems[index].key,
                        value: type === "value" ? value : currentItems[index].value,
                    };
                    return {
                        ...ak,
                        eabnAdTagParameters: currentItems,
                    };
                }
                return ak;
            }),
        );
    }

    function removeAdBreakSelectionParam(id: string, index: number): void {
        onChangeKeys(
            assetKeys.map((ak) => {
                if (ak.tempId === id) {
                    const currentItems = ak.adBreakSelectionParameters;
                    const newItems = currentItems.filter((_item, i) => i !== index);

                    return {
                        ...ak,
                        adBreakSelectionParameters: newItems,
                    };
                }
                return ak;
            }),
        );
    }

    function removeEabnAdTagParam(id: string, index: number): void {
        onChangeKeys(
            assetKeys.map((ak) => {
                if (ak.tempId === id) {
                    const currentItems = ak.eabnAdTagParameters;
                    const newItems = currentItems.filter((_item, i) => i !== index);

                    return {
                        ...ak,
                        eabnAdTagParameters: newItems,
                    };
                }
                return ak;
            }),
        );
    }

    const allKeysUnique = _.uniq(assetKeys.map((ak) => ak.assetKey)).length === assetKeys.length;

    return (
        <div className="flex flex-col gap-10 ">
            {assetKeys.map((ak) => (
                <div
                    className="flex flex-row shadow-md rounded-md border-[1px] border-slate-100 gap-x-6 bg-gray-50"
                    key={`ak-${ak.tempId}`}
                >
                    <div className="flex grow flex-col gap-y-4 px-2 py-1 ">
                        <FormGroup>
                            <HESPFormLabel label="Asset key" />
                            <TextField
                                type="text"
                                fullWidth
                                placeholder="Asset key"
                                variant="outlined"
                                value={ak.assetKey}
                                onChange={(e) => changeAssetKey(ak.tempId, e.target.value)}
                            />
                            {!allKeysUnique && (
                                <div className="text-red-500 text-sm">All asset keys should be unique</div>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <HESPFormLabel label="Ad break selection parameters" />
                            <KeyValueConstructor
                                small
                                items={ak.adBreakSelectionParameters}
                                onAdd={() => addAdBreakSelectionParam(ak.tempId)}
                                onRemove={(index: number) => removeAdBreakSelectionParam(ak.tempId, index)}
                                onUpdate={(type, val, index) =>
                                    updateAdBreakSelectionParam(ak.tempId, type, val, index)
                                }
                            />
                        </FormGroup>

                        <FormGroup>
                            <HESPFormLabel label="EABN tag parameters" />
                            <KeyValueConstructor
                                small
                                items={ak.eabnAdTagParameters}
                                onAdd={() => addEabnAdTagParameters(ak.tempId)}
                                onRemove={(index: number) => removeEabnAdTagParam(ak.tempId, index)}
                                onUpdate={(type, val, index) => updateEabnAdTagParameters(ak.tempId, type, val, index)}
                            />
                        </FormGroup>
                    </div>
                    <div>
                        {assetKeys.length > 1 && (
                            <button
                                className="h-10 w-10 bg-red-500 hover:bg-red-400 flex items-center justify-center rounded-tr-md"
                                onClick={() => onRemoveAssetKey(ak.tempId)}
                            >
                                <DeleteForever className="text-white" color="inherit" />
                            </button>
                        )}
                    </div>
                </div>
            ))}
            <div className="flex w-full items-center justify-center my-6">
                <Button color="secondary" variant="text" startIcon={<Add />} onClick={onAddAssetKey}>
                    Add extra asset key
                </Button>
            </div>
        </div>
    );
}
